import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import image from '@assets/svg/homepageIllustration.svg';
import hertzLogo from '@assets/svg/Logo_50hertz.svg';
import amprionLogo from '@assets/svg/Logo_Amprion.png';
import tennetLogo from '@assets/svg/Logo_Tennet.png';
import transnetLogo from '@assets/svg/Logo_TransnetBW.png';
import { Box, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';

const centerColumnFlex = {
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'flex-start',
};

const openSansText = {
  fontFamily: tsoPqTheme.typography.openSans,
  lineHeight: '1.5625rem',
  fontStyle: 'normal',
  listStyle: 'inside',
  color: tsoPqTheme.palette.customTextColor.color_font_ft_text,
};

const createStyledBox = (styles: any) => styled(Box)(() => styles);

const StyledHomepage = createStyledBox({
  height: '100vh',
  boxSizing: 'border-box',
  width: '100vw',
  ...centerColumnFlex,
  backgroundColor: tsoPqTheme.palette.background.other.color_other_background,
  paddingTop: '2.5rem',
});

const StyledHomepageWrapper = createStyledBox({
  boxSizing: 'border-box',
  height: '100%',
  width: '76.5rem',
  backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
  alignSelf: 'center',
  paddingTop: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_32,
  paddingBottom: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_32,
  gap: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_32,
  ...centerColumnFlex,
});

const StyledHomepageTitle = createStyledBox({
  textAlign: 'center',
  whiteSpace: 'pre-line',
  fontFamily: tsoPqTheme.typography.openSans,
  fontSize: '3rem',
  lineHeight: '4.1875rem',
  fontWeight: '400',
  paddingLeft: '5rem',
  paddingRight: '5rem',
  color: tsoPqTheme.palette.customTextColor.color_primary_font_ft_prim_enabled,
  boxSizing: 'border-box',
  width: '100%',
});

const StyledHomepageImageWrapper = createStyledBox({
  padding: '1.5rem',
  gap: '0.625rem',
  alignSelf: 'center',
  ...centerColumnFlex,
});

const StyledHomepageLogoWrapper = createStyledBox({
  alignSelf: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  maxWidth: '100%',
  gap: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_32,
});

const StyledImage = styled('img')({
  width: '32.75rem',
  height: 'auto',
});

const StyledHomepageTextWrapper = createStyledBox({
  paddingLeft: '5rem',
  paddingRight: '5rem',
  width: '100%',
  boxSizing: 'border-box',
  gap: '1.75rem',
  ...centerColumnFlex,
});

const StyledHomepageDescription = createStyledBox({
  ...openSansText,
  textAlign: 'left',
  width: '75%',
  fontWeight: '400',
  fontSize: '1rem',
});

const StyledHomepageBolded = createStyledBox({
  ...openSansText,
  textAlign: 'left',
  fontWeight: '700',
  fontSize: '1.125rem',
});

const StyledList = styled('ul')({
  width: '100%',
  padding: 0,
  margin: 0,
});

const StyledListItem = styled('li')({
  ...openSansText,
  textAlign: 'left',
  fontWeight: '400',
  fontSize: '1rem',
});

const StyledListContainer = styled(Box)({
  display: 'flex',
  borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
  flexDirection: 'column',
  gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_12,
  alignSelf: 'center',
  padding: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_24,
  backgroundColor: tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_lt,
  boxSizing: 'border-box',
  width: '75%',
});

const StyledHomepageFooterWrapper = createStyledBox({
  boxSizing: 'border-box',
  width: '76.5rem',
  height: '100%',
  backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
  alignSelf: 'center',
  paddingLeft: '6.5rem',
  paddingRight: '6.5rem',
  gap: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_32,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  paddingBottom: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_32,
});

const StyledHomepageFooterTextWrapper = createStyledBox({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_24,
});

const StyledHomepageFooterLine = createStyledBox({
  flex: '1',
  height: '0.016rem',
  backgroundColor: tsoPqTheme.palette.stroke.color_neutral_stroke_str_ntrl_accent,
});

const StyledHomepageFooterDescription = createStyledBox({
  ...openSansText,
  fontSize: '0.875rem',
  textAlign: 'left',
  fontWeight: '400',
});

const StyledHomepageFooterLogo = styled('img')({
  width: '6.125rem',
  height: 'auto',
});

const Home = () => {
  const { t } = useTranslation();

  const environment = import.meta.env.VITE_UENBPQ_ENVIRONMENT;

  return (
    <StyledHomepage>
      <StyledHomepageWrapper>
        <StyledHomepageTitle>{t('homepage.title.' + environment)}</StyledHomepageTitle>
        <StyledHomepageImageWrapper>
          <StyledImage src={image} alt="" />
        </StyledHomepageImageWrapper>
        {environment !== 'prod' && (
          <StyledHomepageTextWrapper>
            <StyledHomepageDescription>{t('homepage.description')}</StyledHomepageDescription>
            <StyledListContainer>
              <StyledHomepageBolded>{t('homepage.testCases.title')}</StyledHomepageBolded>
              <StyledList>
                <StyledListItem>{t('homepage.testCases.first')}</StyledListItem>
                <StyledListItem>{t('homepage.testCases.second')}</StyledListItem>
                <StyledListItem>{t('homepage.testCases.third')}</StyledListItem>
              </StyledList>
            </StyledListContainer>
          </StyledHomepageTextWrapper>
        )}
      </StyledHomepageWrapper>
      <StyledHomepageFooterWrapper>
        <StyledHomepageFooterTextWrapper>
          <StyledHomepageFooterLine />
          <StyledHomepageFooterDescription>{t('homepage.footer.description')}</StyledHomepageFooterDescription>
          <StyledHomepageFooterLine />
        </StyledHomepageFooterTextWrapper>
        <StyledHomepageLogoWrapper>
          <StyledHomepageFooterLogo src={hertzLogo} alt="" />
          <StyledHomepageFooterLogo src={amprionLogo} alt="" />
          <StyledHomepageFooterLogo src={tennetLogo} alt="" />
          <StyledHomepageFooterLogo src={transnetLogo} alt="" />
        </StyledHomepageLogoWrapper>
      </StyledHomepageFooterWrapper>
    </StyledHomepage>
  );
};
export default Home;
